import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, Inbox, Image as ImageIcon, LogOut, Home, Check, X, Trash2, Plus, Eye, Star, UserCog, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, clearToken, CLINIC } from "@/lib/api";

const statusColors = {
  pending: "bg-accent/30 text-accent-foreground",
  confirmed: "bg-primary/20 text-primary",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/15 text-destructive",
};

const StatCard = ({ label, value, subtitle }) => (
  <div className="bg-card border border-border rounded-2xl p-6">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-serif text-4xl mt-2">{value ?? 0}</div>
    {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState({ appointments: {}, enquiries: {}, reviews: {} });
  const [appointments, setAppointments] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ doctor_photo_url: "", doctor_bio: "", doctor_tagline: "" });
  const [newImg, setNewImg] = useState({ url: "", caption: "", category: "clinic" });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { document.title = "Dashboard — Manasvi Admin"; refreshAll(); }, []);

  const refreshAll = async () => {
    try {
      const [s, a, e, g, r, st] = await Promise.all([
        api.get("/stats"), api.get("/appointments"), api.get("/enquiries"),
        api.get("/gallery"), api.get("/reviews/all"), api.get("/settings"),
      ]);
      setStats(s.data); setAppointments(a.data); setEnquiries(e.data);
      setGallery(g.data); setReviews(r.data); setSettings(st.data);
    } catch {
      toast.error("Session expired");
      clearToken(); nav("/admin/login");
    }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}`, { status }); toast.success(`Marked ${status}`); refreshAll(); }
    catch { toast.error("Update failed"); }
  };
  const deleteApt = async (id) => { await api.delete(`/appointments/${id}`); toast.success("Deleted"); refreshAll(); };
  const markRead = async (id) => { await api.patch(`/enquiries/${id}/read`); refreshAll(); };
  const deleteEnq = async (id) => { await api.delete(`/enquiries/${id}`); toast.success("Deleted"); refreshAll(); };
  const addImage = async () => {
    if (!newImg.url) { toast.error("URL required"); return; }
    await api.post("/gallery", newImg); toast.success("Image added");
    setNewImg({ url: "", caption: "", category: "clinic" }); refreshAll();
  };
  const deleteImg = async (id) => { await api.delete(`/gallery/${id}`); toast.success("Removed"); refreshAll(); };
  const approveReview = async (id) => { await api.patch(`/reviews/${id}/approve`); toast.success("Approved & published"); refreshAll(); };
  const unapproveReview = async (id) => { await api.patch(`/reviews/${id}/unapprove`); toast.success("Hidden from site"); refreshAll(); };
  const deleteReview = async (id) => { await api.delete(`/reviews/${id}`); toast.success("Deleted"); refreshAll(); };

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/settings", settings); toast.success("Doctor profile updated"); }
    catch { toast.error("Save failed"); }
    finally { setSavingSettings(false); }
  };

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setSettings(s => ({ ...s, doctor_photo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="bg-background border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={CLINIC.logo} alt="Manasvi Dental" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-serif text-lg leading-none">Admin Dashboard</div>
              <div className="text-[11px] text-muted-foreground">{CLINIC.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/"><Button variant="ghost" size="sm" data-testid="admin-goto-site"><Home className="w-4 h-4 mr-1" />Site</Button></Link>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard label="Appointments" value={stats.appointments?.total} />
          <StatCard label="Pending" value={stats.appointments?.pending} subtitle="Awaiting confirmation" />
          <StatCard label="Confirmed" value={stats.appointments?.confirmed} />
          <StatCard label="Unread Enquiries" value={stats.enquiries?.unread} />
          <StatCard label="Pending Reviews" value={stats.reviews?.pending} subtitle="Awaiting moderation" />
        </div>

        <Tabs defaultValue="appointments">
          <TabsList className="mb-6 flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="appointments" data-testid="tab-appointments"><Calendar className="w-4 h-4 mr-2" />Appointments</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Inbox className="w-4 h-4 mr-2" />Enquiries</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews"><Star className="w-4 h-4 mr-2" />Reviews</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery"><ImageIcon className="w-4 h-4 mr-2" />Gallery</TabsTrigger>
            <TabsTrigger value="doctor" data-testid="tab-doctor"><UserCog className="w-4 h-4 mr-2" />Doctor Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead><TableHead>Contact</TableHead><TableHead>Treatment</TableHead>
                    <TableHead>Date / Time</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No appointments yet</TableCell></TableRow>}
                  {appointments.map(a => (
                    <TableRow key={a.id} data-testid={`apt-row-${a.id}`}>
                      <TableCell><div className="font-medium">{a.name}</div><div className="text-xs text-muted-foreground">{a.email}</div></TableCell>
                      <TableCell><div className="text-sm">{a.phone}</div></TableCell>
                      <TableCell><div className="text-sm">{a.treatment}</div></TableCell>
                      <TableCell><div className="text-sm">{a.date}</div><div className="text-xs text-muted-foreground">{a.time}</div></TableCell>
                      <TableCell><Badge className={a.payment_status === "paid" ? "bg-primary/20 text-primary" : "bg-muted"}>{a.payment_status || "unpaid"}</Badge></TableCell>
                      <TableCell><Badge className={`${statusColors[a.status] || ''} font-normal`}>{a.status}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        {a.status === "pending" && <Button size="sm" variant="ghost" onClick={() => updateStatus(a.id, "confirmed")} data-testid={`confirm-${a.id}`}><Check className="w-4 h-4" /></Button>}
                        {a.status === "confirmed" && <Button size="sm" variant="ghost" onClick={() => updateStatus(a.id, "completed")} data-testid={`complete-${a.id}`}>Done</Button>}
                        {a.status !== "cancelled" && a.status !== "completed" && <Button size="sm" variant="ghost" onClick={() => updateStatus(a.id, "cancelled")} data-testid={`cancel-${a.id}`}><X className="w-4 h-4" /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => deleteApt(a.id)} data-testid={`delete-apt-${a.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="enquiries">
            <div className="space-y-3">
              {enquiries.length === 0 && <div className="bg-card border border-border rounded-2xl p-8 text-center text-muted-foreground">No enquiries yet</div>}
              {enquiries.map(e => (
                <div key={e.id} className={`bg-card border border-border rounded-2xl p-6 ${!e.read ? 'ring-1 ring-primary/40' : ''}`} data-testid={`enq-row-${e.id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{e.name}</span>
                        {!e.read && <Badge className="bg-primary text-primary-foreground text-[10px]">NEW</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{e.email} • {e.phone}</div>
                      <div className="text-xs text-muted-foreground mt-1">Subject: {e.subject}</div>
                    </div>
                    <div className="flex gap-1">
                      {!e.read && <Button size="sm" variant="ghost" onClick={() => markRead(e.id)} data-testid={`read-${e.id}`}><Eye className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => deleteEnq(e.id)} data-testid={`delete-enq-${e.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-foreground/85">{e.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <div className="space-y-3">
              {reviews.length === 0 && <div className="bg-card border border-border rounded-2xl p-8 text-center text-muted-foreground">No reviews yet</div>}
              {reviews.map(r => (
                <div key={r.id} className={`bg-card border border-border rounded-2xl p-6 ${!r.approved ? 'ring-1 ring-accent/50' : ''}`} data-testid={`rev-row-${r.id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{r.name}</span>
                        <div className="flex gap-0.5">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-accent text-accent" />)}</div>
                        {r.approved ? <Badge className="bg-primary/20 text-primary text-[10px]">PUBLISHED</Badge> : <Badge className="bg-accent/30 text-accent-foreground text-[10px]">PENDING</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{r.email || 'no email'} {r.treatment && `• ${r.treatment}`}</div>
                    </div>
                    <div className="flex gap-1">
                      {!r.approved
                        ? <Button size="sm" variant="ghost" onClick={() => approveReview(r.id)} data-testid={`approve-${r.id}`}><Check className="w-4 h-4 text-primary" /></Button>
                        : <Button size="sm" variant="ghost" onClick={() => unapproveReview(r.id)} data-testid={`unapprove-${r.id}`}><X className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} data-testid={`delete-rev-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-foreground/85">{r.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="gallery">
            <div className="mb-6">
              <Dialog>
                <DialogTrigger asChild><Button className="rounded-full" data-testid="add-image-btn"><Plus className="w-4 h-4 mr-2" />Add Image</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Gallery Image</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2"><Label>Image URL*</Label><Input value={newImg.url} onChange={e => setNewImg({...newImg, url: e.target.value})} data-testid="new-image-url" /></div>
                    <div className="space-y-2"><Label>Caption</Label><Input value={newImg.caption} onChange={e => setNewImg({...newImg, caption: e.target.value})} data-testid="new-image-caption" /></div>
                    <div className="space-y-2"><Label>Category</Label><Input value={newImg.category} onChange={e => setNewImg({...newImg, category: e.target.value})} data-testid="new-image-category" /></div>
                    <Button onClick={addImage} className="w-full rounded-full" data-testid="save-image-btn">Add</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map(g => (
                <div key={g.id} className="relative group rounded-2xl overflow-hidden border border-border" data-testid={`gallery-admin-${g.id}`}>
                  <img src={g.url} alt={g.caption} className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button size="sm" variant="destructive" onClick={() => deleteImg(g.id)} data-testid={`delete-img-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  {g.caption && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-foreground/70 to-transparent text-background text-xs p-2">{g.caption}</div>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="doctor">
            <div className="bg-card border border-border rounded-2xl p-8 max-w-3xl">
              <h2 className="font-serif text-2xl mb-6">Doctor Profile</h2>
              <div className="grid md:grid-cols-3 gap-6 items-start">
                <div className="md:col-span-1">
                  <div className="aspect-[4/5] rounded-2xl bg-muted overflow-hidden border border-border">
                    {settings.doctor_photo_url && <img src={settings.doctor_photo_url} alt="Doctor" className="w-full h-full object-cover" data-testid="admin-doctor-preview" />}
                  </div>
                  <label className="mt-4 inline-flex w-full">
                    <span className="inline-flex items-center justify-center w-full rounded-full border border-border bg-muted/60 hover:bg-muted px-4 py-2 text-sm font-medium cursor-pointer transition-colors">
                      <Upload className="w-4 h-4 mr-2" /> Upload New Photo
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="admin-doctor-upload" />
                  </label>
                  <p className="text-xs text-muted-foreground mt-2 text-center">Or paste a URL below</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div className="space-y-2">
                    <Label>Photo URL</Label>
                    <Input value={settings.doctor_photo_url || ""} onChange={e => setSettings(s => ({...s, doctor_photo_url: e.target.value}))} data-testid="admin-doctor-url" placeholder="https://..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline / Credentials</Label>
                    <Input value={settings.doctor_tagline || ""} onChange={e => setSettings(s => ({...s, doctor_tagline: e.target.value}))} data-testid="admin-doctor-tagline" placeholder="BDS, MDS — Consultant Dentist" />
                  </div>
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea rows={5} value={settings.doctor_bio || ""} onChange={e => setSettings(s => ({...s, doctor_bio: e.target.value}))} data-testid="admin-doctor-bio" />
                  </div>
                  <Button onClick={saveSettings} className="rounded-full bg-primary hover:bg-primary/90 px-8" disabled={savingSettings} data-testid="admin-doctor-save">
                    {savingSettings ? "Saving..." : "Save Profile"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
